/**
 * 1.6.0 the agent API gateway: Administration → Webhooks → "Site services (through storage agents)". Stream W's file
 * (WebhooksAdmin.tsx only mounts it; docs/dev/mcp-1.6.0-build.md).
 *
 * Read-only, filled in from the agents' reports (useSiteServices): per agent its gateway state (off, on, file error,
 * agent too old), then its services: name, address (origin only), methods, paths, description, used by, last call;
 * Test (admin: useTestSiteService, a HEAD or GET of the health path, ≤ 25 s, shows the status and the time). It
 * explains how to turn the gateway on in the agent's own configuration (PRISM_AGENT_GATEWAY=on, an example
 * gateway.json) and that services are added on the site, never from PRISM.
 */
import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Loader2, Network, Send } from 'lucide-react';
import type { AgentGatewayState, AgentSiteServicesDTO, SiteServiceDTO, SiteServiceTestResult } from '@prism/contracts';
import { errorMessage } from '../../../api/client';
import { useSiteServices, useTestSiteService } from '../../../api/hooks/site-services';
import { StatusDot } from '../../../components/Status';
import { relative } from '../../../lib/format';

const STATE: Record<AgentGatewayState, { label: string; tone: string }> = {
  on: { label: 'Gateway on', tone: 'ready' },
  off: { label: 'Gateway off', tone: 'queued' },
  file_error: { label: 'gateway.json refused', tone: 'failed' },
  too_old: { label: 'Agent too old', tone: 'queued' },
};

const EXAMPLE = `{
  "services": [
    {
      "name": "newsroom",
      "baseUrl": "https://newsroom.internal:8443/api",
      "methods": ["GET", "POST"],
      "paths": ["/stories/*", "/rundowns"],
      "headers": { "Authorization": "env:NEWSROOM_TOKEN" },
      "healthPath": "/rundowns",
      "description": "Newsroom system: stories and rundowns"
    }
  ]
}`;

type TestOutcome = { result: SiteServiceTestResult } | { error: string };

export function SiteServicesSection() {
  const q = useSiteServices();
  const test = useTestSiteService();
  const [busy, setBusy] = useState<string | null>(null);
  const [outcomes, setOutcomes] = useState<Record<string, TestOutcome>>({});
  const [showHow, setShowHow] = useState(false);
  if (q.isError) return null;
  const agents = q.data?.items ?? [];

  async function runTest(a: AgentSiteServicesDTO, s: SiteServiceDTO) {
    const key = `${a.agentId}/${s.name}`;
    setBusy(key);
    try {
      const result = await test.mutateAsync({ agentId: a.agentId, service: s.name });
      setOutcomes((o) => ({ ...o, [key]: { result } }));
    } catch (err) {
      setOutcomes((o) => ({ ...o, [key]: { error: errorMessage(err) } }));
    } finally {
      setBusy(null);
    }
  }

  return (
    <section className="site-services" data-testid="site-services" aria-labelledby="site-services-title">
      <h3 id="site-services-title" style={{ marginTop: 32 }}><Network size={16} /> Site services (through storage agents)</h3>
      <p className="lede" style={{ marginTop: 4 }}>
        Systems inside a site’s network (a newsroom system, a playout server) that workflows reach through that site’s storage agent with a
        <b> Call site service</b> or <b>Repeat until</b> step. Nothing is opened inbound: each call travels down the agent’s own connection.
        The site decides what may be called: services, methods, paths and their secret headers are listed in the agent’s <span className="mono">gateway.json</span>,
        never in PRISM, and PRISM never sees the secrets.
        {' '}<button type="button" className="btn ghost sm" onClick={() => setShowHow((v) => !v)} aria-expanded={showHow} data-testid="site-services-how">How to turn it on</button>
      </p>
      {showHow && (
        <div className="site-services-how" data-testid="site-services-how-text">
          <p>On the site server, in the agent’s configuration: set <span className="mono">PRISM_AGENT_GATEWAY=on</span> and
            {' '}<span className="mono">PRISM_AGENT_GATEWAY_FILE=gateway.json</span> (or run <span className="mono">prism-agent configure</span>), write the file, check it with
            {' '}<span className="mono">prism-agent gateway check</span> and restart the agent. It reports its services here within a minute. For example:</p>
          <pre className="mono">{EXAMPLE}</pre>
          <p className="faint">Header values are read on the agent from its environment (<span className="mono">env:NAME</span>) or a file (<span className="mono">file:path</span>).</p>
        </div>
      )}
      {q.isLoading ? <p className="muted"><Loader2 size={13} className="spin" /> Loading…</p>
        : agents.length === 0 ? <p className="muted" data-testid="site-services-empty">No storage agents yet. <Link to="/admin/agents">Storage agents</Link> are added under Administration.</p>
          : agents.map((a) => (
            <div key={a.agentId} className="site-services-agent" data-testid="site-services-agent" data-agent={a.agentName}>
              <div className="site-services-agent-head">
                <strong>{a.agentName}</strong> <span className="faint">{a.site}</span>
                <StatusDot tone={STATE[a.state].tone} data-testid="gateway-state" data-state={a.state}>{STATE[a.state].label}</StatusDot>
                {!a.connected && <span className="pill">Not connected</span>}
              </div>
              {a.state === 'file_error' && <div className="site-services-error" role="alert">{a.fileError}</div>}
              {a.state === 'off' && <p className="faint site-services-note">The gateway is off in this agent’s configuration.</p>}
              {a.state === 'too_old' && <p className="faint site-services-note">Update this agent to 1.6.0 or later to use site services.</p>}
              {a.state === 'on' && (a.services.length === 0
                ? <p className="faint site-services-note">The agent’s gateway.json lists no services.</p>
                : (
                  <table className="table" aria-label={`Site services of ${a.agentName}`}>
                    <thead><tr><th>Service</th><th>Address</th><th>Allows</th><th>Used by</th><th>Last call</th><th aria-label="Actions" /></tr></thead>
                    <tbody>{a.services.map((s) => {
                      const key = `${a.agentId}/${s.name}`;
                      const out = outcomes[key];
                      return (
                        <tr key={s.name} data-testid="site-service-row" data-service={s.name} style={{ cursor: 'default' }}>
                          <td style={{ whiteSpace: 'normal' }}><strong className="mono">{s.name}</strong>
                            {s.description && <div className="faint" style={{ fontSize: 12 }}>{s.description}</div>}</td>
                          <td className="mono" style={{ fontSize: 12 }}>{s.origin}</td>
                          <td style={{ whiteSpace: 'normal' }}>
                            <div className="site-services-methods">{s.methods.map((m) => <span key={m} className="pill">{m}</span>)}</div>
                            <div className="mono faint" style={{ fontSize: 12 }}>{s.paths.join('  ')}</div>
                          </td>
                          <td className="muted" style={{ whiteSpace: 'normal' }}>{s.usedBy.length ? s.usedBy.map((w) => w.name).join(', ') : 'No workflows'}</td>
                          <td className="muted" style={{ whiteSpace: 'normal' }}>{s.lastCall
                            ? <><StatusDot tone={s.lastCall.error || (s.lastCall.status ?? 0) >= 400 ? 'failed' : 'ready'}>{s.lastCall.status ?? 'no answer'}</StatusDot> {relative(s.lastCall.at)}
                              {s.lastCall.error && <div style={{ fontSize: 12, color: 'var(--danger)' }}>{s.lastCall.error}</div>}</>
                            : 'Never'}</td>
                          <td style={{ textAlign: 'right' }}>
                            <button className="btn ghost sm" disabled={busy !== null || !a.connected} title={a.connected ? 'Send a HEAD or GET of the health path through the agent' : 'The agent is not connected'}
                              onClick={() => void runTest(a, s)} data-testid="test-site-service">
                              {busy === key ? <Loader2 size={13} className="spin" /> : <Send size={13} />} Test</button>
                            {out && <TestLine outcome={out} />}
                          </td>
                        </tr>
                      );
                    })}</tbody>
                  </table>
                ))}
            </div>
          ))}
    </section>
  );
}

function TestLine({ outcome }: { outcome: TestOutcome }) {
  if ('error' in outcome) return <div className="site-services-test failed" data-testid="site-service-test-result" role="status">{outcome.error}</div>;
  const r = outcome.result;
  return (
    <div className={`site-services-test ${r.ok ? 'ok' : 'failed'}`} data-testid="site-service-test-result" role="status">
      <span className="mono">{r.method} {r.path}</span>{' → '}
      {r.status !== null ? <b>{r.status}</b> : <b>{r.error?.code ?? 'failed'}</b>}
      {r.durationMs !== null && <span className="faint"> in {r.durationMs} ms</span>}
      {r.error && <div className="faint">{r.error.message}</div>}
    </div>
  );
}
